import { useCallback, useEffect, useState } from 'react';
import { useResource } from '../../lib/useResource.js';
import { put } from '../../lib/api.js';
import { formatDateTime } from '../../lib/format.js';
import {
  Alert,
  Badge,
  Card,
  ErrorBox,
  useToast,
} from '../../components/ui.js';
import { Tabela } from '../../components/Tabela.js';
import { useSession } from '../../lib/session.js';
import {
  ativarPush,
  desativarPush,
  enviarTeste,
  estaInstalado,
  ehIOS,
  situacaoPush,
  type SituacaoPush,
} from '../../lib/push.js';

interface Dispositivo {
  id: string;
  endpoint: string;
  user_agent: string | null;
  created_at: string;
  last_success_at: string | null;
  failure_count: number;
}

const EVENTOS: { chave: string; titulo: string; descricao: string }[] = [
  {
    chave: 'waiting_human',
    titulo: 'Cliente aguardando pessoa',
    descricao: 'O aviso mais importante: alguém pediu atendimento humano.',
  },
  {
    chave: 'inbound_message',
    titulo: 'Mensagem recebida',
    descricao: 'Passa a valer quando a conexão de WhatsApp for ligada (etapa E3).',
  },
  {
    chave: 'reminder_failed',
    titulo: 'Lembrete travado',
    descricao: 'Falha de envio, entrega incerta ou falta de dado na mensagem.',
  },
  {
    chave: 'booking_changed',
    titulo: 'Mudança na agenda',
    descricao: 'Reserva criada, remarcada ou cancelada.',
  },
];

function nomeDoAparelho(userAgent: string | null): string {
  if (!userAgent) return 'Aparelho não identificado';
  if (/iPhone/.test(userAgent)) return 'iPhone';
  if (/iPad/.test(userAgent)) return 'iPad';
  if (/Android/.test(userAgent)) return 'Android';
  if (/Macintosh/.test(userAgent)) return 'Mac';
  if (/Windows/.test(userAgent)) return 'Windows';
  return 'Outro aparelho';
}

export function Notificacoes() {
  const { can } = useSession();
  const toast = useToast();
  const [situacao, setSituacao] = useState<SituacaoPush | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const devices = useResource<{ devices: Dispositivo[]; events: Record<string, boolean> }>(
    '/api/push/devices',
  );

  const atualizar = useCallback(async () => {
    setSituacao(await situacaoPush());
  }, []);

  useEffect(() => {
    void atualizar();
  }, [atualizar]);

  async function comTratamento(acao: () => Promise<unknown>) {
    setOcupado(true);
    setErro(null);
    try {
      await acao();
      await atualizar();
      devices.reload();
    } catch (err) {
      setErro(err);
    } finally {
      setOcupado(false);
    }
  }

  const eventos = devices.data?.events ?? {};

  return (
    <>
      <Card
        title="Avisos neste aparelho"
        description="Para ser chamado quando um cliente precisar de pessoa, mesmo com o painel fechado."
      >
        <ErrorBox error={erro} />

        {situacao?.estado === 'precisa_instalar_ios' ? (
          <Alert tone="warning" title="Instale o painel na Tela de Início">
            <p>No iPhone e no iPad, os avisos só existem com o painel instalado como aplicativo:</p>
            <ol style={{ paddingLeft: 20, margin: '6px 0 0' }}>
              <li>Toque no botão Compartilhar do Safari.</li>
              <li>Escolha "Adicionar à Tela de Início".</li>
              <li>Abra o painel por esse ícone e volte aqui para ativar os avisos.</li>
            </ol>
            <p className="small muted" style={{ marginTop: 8 }}>
              Requer iOS 16.4 ou mais novo. Em aba comum do Safari, a Apple não oferece notificação.
            </p>
          </Alert>
        ) : null}

        {situacao?.estado === 'sem_suporte' ? (
          <Alert tone="warning" title="Este navegador não oferece avisos">
            {situacao.explicacao}
          </Alert>
        ) : null}

        {situacao?.estado === 'negado' ? (
          <Alert tone="error" title="Avisos bloqueados neste aparelho">
            {situacao.explicacao}
          </Alert>
        ) : null}

        {situacao?.estado === 'disponivel' ? (
          <>
            <p>{situacao.explicacao}</p>
            <button
              type="button"
              className="primary"
              disabled={ocupado}
              onClick={() =>
                void comTratamento(async () => {
                  const nova = await ativarPush();
                  if (nova.estado === 'ativo') toast('Avisos ativados neste aparelho.');
                })
              }
            >
              {ocupado ? 'Ativando…' : 'Ativar avisos neste aparelho'}
            </button>
          </>
        ) : null}

        {situacao?.estado === 'ativo' ? (
          <>
            <p>
              <Badge tone="ok">ativo neste aparelho</Badge>
            </p>
            <div className="row">
              <button
                type="button"
                disabled={ocupado}
                onClick={() =>
                  void comTratamento(async () => {
                    const resultado = await enviarTeste();
                    toast(
                      resultado.accepted > 0
                        ? 'Aviso de teste enviado. Ele deve chegar em alguns segundos.'
                        : 'Nenhum aparelho recebeu o teste. Confira a lista abaixo.',
                      resultado.accepted > 0 ? 'ok' : 'error',
                    );
                  })
                }
              >
                Enviar aviso de teste
              </button>
              <button
                type="button"
                disabled={ocupado}
                onClick={() =>
                  void comTratamento(async () => {
                    await desativarPush();
                    toast('Avisos desativados neste aparelho.');
                  })
                }
              >
                Desativar neste aparelho
              </button>
            </div>
          </>
        ) : null}

        <Alert tone="info" title="O que o aviso não garante">
          A entrega depende do serviço da Apple ou do Google e pode falhar sem que o sistema saiba.
          Por isso a lista de avisos do painel continua sendo a fonte de verdade: nada é considerado
          resolvido por ter saído um push.
        </Alert>

        {!estaInstalado() && !ehIOS() ? (
          <p className="small muted">
            No Android e no computador, o navegador oferece instalar o painel como aplicativo. Assim
            ele abre em tela cheia e os avisos continuam funcionando com o painel fechado.
          </p>
        ) : null}
      </Card>

      <Card title="Aparelhos que recebem avisos" tight>
        <ErrorBox error={devices.error} />
        <Tabela
          linhas={devices.data?.devices ?? []}
          chave={(d) => d.id}
          vazio="Nenhum aparelho registrado ainda."
          vazioDetalhe="Ative os avisos no celular que você usa no dia a dia."
          colunas={[
            {
              chave: 'aparelho',
              rotulo: 'Aparelho',
              tipo: 'principal',
              render: (d) => nomeDoAparelho(d.user_agent),
            },
            { chave: 'registro', rotulo: 'Registrado em', render: (d) => formatDateTime(d.created_at) },
            {
              chave: 'ultimo',
              rotulo: 'Último aviso aceito',
              render: (d) => formatDateTime(d.last_success_at),
            },
            {
              chave: 'falhas',
              rotulo: 'Falhas seguidas',
              tipo: 'num',
              render: (d) =>
                d.failure_count > 0 ? <Badge tone="attention">{d.failure_count}</Badge> : '0',
            },
          ]}
        />
      </Card>

      <Card title="Quando avisar" description="Vale para toda a equipe desta loja.">
        {EVENTOS.map((evento) => (
          <label className="checkbox" key={evento.chave}>
            <input
              type="checkbox"
              disabled={!can('company.settings.write')}
              checked={eventos[evento.chave] !== false}
              onChange={async (e) => {
                await put('/api/push/events', { [evento.chave]: e.target.checked });
                devices.reload();
                toast('Preferência salva.');
              }}
            />
            <span>
              <strong>{evento.titulo}</strong>
              <div className="small muted">{evento.descricao}</div>
            </span>
          </label>
        ))}
        {!can('company.settings.write') ? (
          <p className="small muted">Somente proprietário ou administrador altera estas opções.</p>
        ) : null}
      </Card>
    </>
  );
}
